<script setup lang="ts">
  import { computed, useSlots } from 'vue';
  import { TableHeader, FilterSortAndDelete, TableTitle } from '@/shared/ui';
  import type { OrderOptions } from '@/shared/lib';

  const props = withDefaults(
    defineProps<{
      headers: string[];
      itemCount: number;
      itemType: string;
      hasSelected: boolean;
      selectedCount: number;
      selectedDocuments: number[];
      allSelected: boolean;
      partiallySelected?: boolean;
      orderOptions: OrderOptions[];
      isFilterActive?: boolean;
      currentSort?: string;
      currentOrder?: string;
      customMessage?: string;
      title?: string;
      showItemCount?: boolean;
      showColumnHeaders?: boolean;
      thClass?: string | ((header: string, index: number) => string | undefined);
      thCheckboxClass?: string;
      hasSelection?: boolean;
      hasSort?: boolean;
      hasFilter?: boolean;
      hasDelete?: boolean;
      deleteDisabled?: boolean;
      hasEdit?: boolean;
      editDisabled?: boolean;
    }>(),
    {
      isFilterActive: false,
      partiallySelected: false,
      showItemCount: true,
      showColumnHeaders: true,
      hasSelection: true,
      hasSort: true,
      hasFilter: true,
      hasDelete: true,
      deleteDisabled: false,
      hasEdit: false,
      editDisabled: false,
    }
  );

  const emit = defineEmits<{
    'toggle-all': [];
    'update:sort': [sortCamp: string, orderFlow: string];
    'update:filter': [filters: Record<string, unknown>];
    'filter-clicked': [];
    delete: [];
    edit: [];
  }>();

  const slots = useSlots();
  const hasTabsSlot = computed(() => Boolean(slots.tabs));

  const hasTitleOrCustomMessage = computed(() => Boolean(props.title || props.customMessage));

  const titleColspan = computed(() => props.headers.length + (props.hasSelection ? 1 : 0));

  const displayText = computed(() => {
    const titleOrCustom = props.title ?? props.customMessage;
    if (titleOrCustom) {
      return titleOrCustom;
    }

    if (props.showItemCount) {
      return `${props.itemCount} ${props.itemType}`;
    }

    return '';
  });

  const getThClass = (header: string, index: number) => {
    if (typeof props.thClass === 'function') {
      return props.thClass(header, index);
    }
    return props.thClass;
  };

  const handleToggleAll = () => {
    emit('toggle-all');
  };

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    emit('update:sort', sortCamp, orderFlow);
  };

  const handleFilter = (filters: Record<string, unknown>) => {
    emit('update:filter', filters);
  };

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  const handleDelete = () => {
    emit('delete');
  };

  const handleEdit = () => {
    emit('edit');
  };
</script>

<template>
  <thead class="bg-white-01 divide-y divide-gray-02">
    <tr>
      <td
        :colspan="titleColspan"
        class="px-6 py-4"
      >
        <div
          :class="[
            'flex w-full items-center',
            displayText || hasTabsSlot ? ' justify-between' : ' justify-end',
          ]"
        >
          <slot name="tabs">
            <TableTitle
              v-if="displayText"
              :text="displayText"
              :muted="!hasTitleOrCustomMessage"
            />
          </slot>
          <FilterSortAndDelete
            :has-selected="hasSelected"
            :selected-count="selectedCount"
            :selected-documents="selectedDocuments"
            :order-options="orderOptions"
            :current-sort="currentSort"
            :current-order="currentOrder"
            :is-filter-active="isFilterActive"
            :has-sort="hasSort"
            :has-filter="hasFilter"
            :has-delete="hasDelete"
            :delete-disabled="deleteDisabled"
            :has-edit="hasEdit"
            :edit-disabled="editDisabled"
            @update:sort="handleSortOptions"
            @update:filter="handleFilter"
            @filter-clicked="handleFilterClicked"
            @delete="handleDelete"
            @edit="handleEdit"
          />
        </div>
      </td>
    </tr>
    <tr v-if="showColumnHeaders">
      <TableHeader
        v-for="(header, index) in headers"
        :key="header"
        :text="header"
        :checkbox="hasSelection && index === 0"
        :checked="allSelected"
        :indeterminate="partiallySelected"
        :th-class="getThClass(header, index)"
        :th-checkbox-class="thCheckboxClass"
        @toggle-all="handleToggleAll"
      />
    </tr>
  </thead>
</template>
